import { useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { BilledListPanel } from "@/components/properties/billed-list-panel";
import { DevicesListPanel } from "@/components/properties/devices-list-panel";
import { PropertiesListPanel } from "@/components/properties/properties-list-panel";
import { PageTitle, SegmentedTabs } from "@/components/ui";
import {
  PROPERTIES_TABS,
  propertiesTabSearch,
  validatePropertiesSearch,
  type PropertiesSearchWriter,
  type PropertiesTab,
} from "@/lib/properties-search";

export const Route = createFileRoute("/_customer/properties/")({
  validateSearch: validatePropertiesSearch,
  component: PropertiesPage,
});

/** Ties each tab to its panel: `properties-tab-<tab>` controls `properties-panel-<tab>`. */
const ID_PREFIX = "properties";

const TAB_LABEL_KEY: Record<PropertiesTab, string> = {
  properties: "properties.tabs.properties",
  devices: "properties.tabs.devices",
  billed: "properties.tabs.billed",
};

/**
 * The Properties list: three tabs of one page, the visible one named by the
 * `tab` search param. A tab switch navigates to that tab with none of its
 * filters; each tab's body owns its filter bar and its reads.
 */
function PropertiesPage() {
  const { t } = useTranslation("customer");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const tab = search.tab ?? "properties";

  const onSearch = useCallback<PropertiesSearchWriter>(
    (next, options) => void navigate({ to: "/properties", search: next, ...options }),
    [navigate],
  );

  return (
    <div className="flex flex-col gap-5 lg:gap-6">
      <PageTitle title={t("properties.title")}>
        <SegmentedTabs
          tabs={PROPERTIES_TABS.map((value) => ({ value, label: t(TAB_LABEL_KEY[value]) }))}
          value={tab}
          onChange={(value) => onSearch(propertiesTabSearch(value))}
          ariaLabel={t("properties.tabsLabel")}
          idPrefix={ID_PREFIX}
        />
      </PageTitle>
      <div
        role="tabpanel"
        id={`${ID_PREFIX}-panel-${tab}`}
        aria-labelledby={`${ID_PREFIX}-tab-${tab}`}
        tabIndex={0}
        className="flex flex-col gap-4"
      >
        {search.tab === "devices" ? (
          <DevicesListPanel search={search} onSearch={onSearch} />
        ) : search.tab === "billed" ? (
          <BilledListPanel search={search} onSearch={onSearch} />
        ) : (
          <PropertiesListPanel search={search} onSearch={onSearch} />
        )}
      </div>
    </div>
  );
}
